import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  InitialSetupSessionResponse,
  InitialSetupStatusResponse,
  InitialSetupStep1Request,
  InitialSetupStep1Response,
  InitialSetupStep2Request,
  InitialSetupStep3Request,
} from '../models/initial-setup.model';
import { ApiResponse } from '@shared/models/api-response.models';

/**
 * All HTTP calls in this service return the standard
 * `ApiResponse<T>` envelope.
 */

const INITIAL_SETUP_SESSION_KEY = 'initialSetup.session';

interface StoredInitialSetupSession {
  sessionId: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class InitialSetupService {
  private apiUrl = environment.userManagementApiUrl;
  private status$?: Observable<ApiResponse<InitialSetupStatusResponse>>;

  constructor(private http: HttpClient) {}

  getStatus(): Observable<ApiResponse<InitialSetupStatusResponse>> {
    // This endpoint is used by routing guards and can be requested multiple times
    // during navigations. Cache+share the latest value for the lifetime of the app.
    this.status$ ??= this.http
      .get<
        ApiResponse<InitialSetupStatusResponse>
      >(`${this.apiUrl}/api/initial-setup/v1/status`)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));

    return this.status$;
  }

  /**
   * Drops the cached status so the next guard run asks the platform again.
   *
   * The cache above is right for every navigation except one: the one the
   * wizard performs when setup completes, which is the event that changes the
   * answer. Without this, the guard replayed "setup required" from the app's
   * first navigation and sent the new owner back into the wizard they had
   * just finished (F-019). Called from the step-3 success handler only —
   * session expiry and a failed restore leave setup genuinely required.
   */
  invalidateStatus(): void {
    this.status$ = undefined;
  }

  step1(
    payload: InitialSetupStep1Request
  ): Observable<ApiResponse<InitialSetupStep1Response>> {
    return this.http
      .post<
        ApiResponse<InitialSetupStep1Response>
      >(`${this.apiUrl}/api/initial-setup/v1/step1`, payload)
      .pipe(
        tap(res => {
          const data = res?.data;
          if (data?.sessionId) {
            this.saveSession(data.sessionId, data.expiresInSeconds);
          }
        })
      );
  }

  step2(
    sessionId: string,
    payload: InitialSetupStep2Request
  ): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      `${this.apiUrl}/api/initial-setup/v1/session/${encodeURIComponent(sessionId)}/step2`,
      payload
    );
  }

  step3(
    sessionId: string,
    payload: InitialSetupStep3Request
  ): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      `${this.apiUrl}/api/initial-setup/v1/session/${encodeURIComponent(sessionId)}/step3`,
      payload
    );
  }

  getSession(
    sessionId: string
  ): Observable<ApiResponse<InitialSetupSessionResponse>> {
    return this.http.get<ApiResponse<InitialSetupSessionResponse>>(
      `${this.apiUrl}/api/initial-setup/v1/session/${encodeURIComponent(sessionId)}`
    );
  }

  /**
   * Returns the stored sessionId only if it has not expired yet.
   * Expired sessions are cleared automatically.
   */
  getSessionId(): string | null {
    const stored = this.readStoredSession();
    if (!stored) {
      return null;
    }
    if (this.isExpired(stored.expiresAt)) {
      this.clearSession();
      return null;
    }
    return stored.sessionId;
  }

  hasValidSession(): boolean {
    return this.getSessionId() !== null;
  }

  clearSession(): void {
    try {
      localStorage.removeItem(INITIAL_SETUP_SESSION_KEY);
    } catch {
      // ignore
    }
  }

  private saveSession(sessionId: string, expiresInSeconds: number): void {
    try {
      const expiresAt = Date.now() + expiresInSeconds * 1000;
      const data: StoredInitialSetupSession = { sessionId, expiresAt };
      localStorage.setItem(INITIAL_SETUP_SESSION_KEY, JSON.stringify(data));
    } catch {
      // ignore
    }
  }

  private readStoredSession(): StoredInitialSetupSession | null {
    try {
      const raw = localStorage.getItem(INITIAL_SETUP_SESSION_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as StoredInitialSetupSession;
      if (
        !parsed ||
        typeof parsed.sessionId !== 'string' ||
        typeof parsed.expiresAt !== 'number'
      ) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private isExpired(expiresAt: number): boolean {
    return Date.now() >= expiresAt;
  }
}
